import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Badge,
  CircularProgress,
} from "@mui/material";
import { Notifications } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useNotificationsQuery,
  useSetNotificationSeenMutation,
  useUnreadNotificationsCountQuery,
  useUserNotificationSubscriptionSubscription,
} from "../../../__generated__/graphql";
import {
  getCurrentUserId,
  isNotificationUnreadForUser,
} from "../../../services/notifications/unread";
import { getInAppNotificationPath } from "../../../services/notifications/link";

// Only http(s) absolute URLs and same-origin relative paths are navigable;
// dangerous schemes (javascript:, data:, vbscript:, file:, ...) and malformed
// strings are rejected.
const SAFE_URL_SCHEMES = new Set(["http:", "https:"]);

function getSafeNotificationLink(link?: string | null): string | null {
  if (!link) return null;
  const trimmed = link.trim();
  if (!trimmed) return null;

  try {
    const resolved = new URL(trimmed, window.location.origin);
    return SAFE_URL_SCHEMES.has(resolved.protocol) ? trimmed : null;
  } catch {
    return null;
  }
}

// The dropdown only previews the newest few; the rest live on /notifications.
const MAX_DROPDOWN_NOTIFICATIONS = 5;

// The fields the bell renders; both the paginated query and the real-time
// subscription event can be mapped into this shape.
interface BellNotification {
  id: string;
  title: string;
  body: string;
  link?: string | null;
  seen?: readonly (string | null | undefined)[] | null;
  metadata?: {
    createdAt?: any;
    updatedAt?: any;
  } | null;
}

// Helper function to format time ago
function getTimeAgo(date: Date): string {
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

  return date.toLocaleDateString();
}

export default function NotificationsBell() {
  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<HTMLElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  const currentUserId = getCurrentUserId();

  // Display state, seeded from the server and updated in real time below.
  const [items, setItems] = useState<BellNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const seenIdsRef = useRef<Set<string>>(new Set());

  const {
    data: notificationsData,
    loading: notificationsLoading,
    refetch: refetchNotifications,
  } = useNotificationsQuery({
    variables: { limit: MAX_DROPDOWN_NOTIFICATIONS, offset: 0 },
    fetchPolicy: "network-only",
  });

  const { data: unreadData, refetch: refetchUnreadCount } =
    useUnreadNotificationsCountQuery();

  const [setNotificationSeenMutation] = useSetNotificationSeenMutation();

  // Subscribe to real-time notifications
  const { data: subscriptionData } =
    useUserNotificationSubscriptionSubscription();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Seed the dropdown and badge from the server.
  useEffect(() => {
    setItems(notificationsData?.notifications?.items ?? []);
  }, [notificationsData]);

  useEffect(() => {
    setUnreadCount(unreadData?.unreadNotificationsCount ?? 0);
  }, [unreadData]);

  useEffect(() => {
    seenIdsRef.current = new Set(items.map((n) => n.id));
  }, [items]);

  // Real-time: put genuinely new notifications at the top (no duplicates) and
  // bump the unread badge only for an unread, previously-unseen event.
  useEffect(() => {
    const event = subscriptionData?.userNotification;
    if (!event?.id) return;
    if (seenIdsRef.current.has(event.id)) return;

    if (isNotificationUnreadForUser(event, currentUserId)) {
      setUnreadCount((prev) => prev + 1);
    }
    setItems((prev) => [event, ...prev].slice(0, MAX_DROPDOWN_NOTIFICATIONS));
  }, [subscriptionData, currentUserId]);

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!mounted) return;
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    if (!mounted) return;
    setNotificationAnchorEl(null);
  };

  const markNotificationSeen = (notificationId: string) => {
    const wasUnread = items.some(
      (n) =>
        n.id === notificationId &&
        isNotificationUnreadForUser(n, currentUserId),
    );

    setNotificationSeenMutation({ variables: { notificationId } })
      .then(() => {
        // Reflect the change immediately; refetch the authoritative count so
        // the badge can't drift from double-decrements or stale data.
        if (wasUnread) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
        setItems((prev) =>
          prev.map((n) =>
            n.id === notificationId
              ? {
                  ...n,
                  seen: Array.from(
                    new Set([...(n.seen ?? []), currentUserId].filter(Boolean)),
                  ),
                }
              : n,
          ),
        );
        refetchNotifications();
        refetchUnreadCount();
      })
      .catch((error) => {
        console.error("Failed to mark notification as seen:", error);
      });
  };

  // Clicking anywhere on the card: mark as seen, then open its destination.
  // App links open in the same tab, while external links open in a new tab.
  const handleCardClick = (notificationId: string, link?: string | null) => {
    markNotificationSeen(notificationId);
    handleNotificationClose();
    const inAppPath = getInAppNotificationPath(link);
    if (inAppPath) {
      navigate(inAppPath);
      return;
    }
    const safeLink = getSafeNotificationLink(link);
    if (safeLink) {
      window.open(safeLink, "_blank", "noopener,noreferrer");
    }
  };

  const handleViewAllClick = () => {
    handleNotificationClose();
    navigate("/notifications");
  };

  // The dropdown renders the newest few; the server already sorts them
  // newest-first, and real-time events are prepended above.
  const latestNotifications = items;

  if (!mounted) return null;

  return (
    <>
      {/* Notifications Bell Button */}
      <IconButton
        data-testid="notifications-bell-button"
        onClick={handleNotificationOpen}
        sx={{
          color: "#6B7280",
          p: { xs: 0.5, sm: 0.75 },
          "&:hover": { backgroundColor: "rgba(107, 114, 128, 0.08)" },
          position: "relative",
        }}
      >
        <Badge
          data-testid="notifications-bell-badge-span"
          badgeContent={unreadCount}
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: unreadCount > 0 ? "#EF4444" : "#D1D5DB",
              color: "#FFFFFF",
              fontSize: { xs: "0.55rem", sm: "0.625rem" },
              minWidth: { xs: 12, sm: 14 },
              height: { xs: 12, sm: 14 },
            },
          }}
        >
          <Notifications sx={{ fontSize: { xs: 14, sm: 16 } }} />
        </Badge>
      </IconButton>

      {/* Notifications Menu */}
      <Menu
        data-testid="notifications-menu-div"
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            mt: { xs: 1, md: 1.25 },
            borderRadius: { xs: 1.25, md: 1.5 },
            border: "1px solid #E5E7EB",
            maxWidth: { xs: 280, sm: 360, md: 400 },
            maxHeight: { xs: 400, md: 500 },
            width: { xs: 280, sm: 360, md: 400 },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {notificationsLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 3,
              px: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : latestNotifications.length === 0 ? (
          <Box
            sx={{
              py: 2,
              px: 2,
              textAlign: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#6B7280",
                fontSize: { xs: "0.75rem", sm: "0.8125rem" },
              }}
            >
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: { xs: 360, md: 450 }, overflowY: "auto" }}>
            {latestNotifications.map((notification, index) => {
              // Determine whether the current user has seen this notification.
              const isSeen = !isNotificationUnreadForUser(
                notification,
                currentUserId,
              );
              const createdAt = notification.metadata?.createdAt
                ? new Date(notification.metadata.createdAt)
                : null;
              const timeAgo = createdAt ? getTimeAgo(createdAt) : "Recently";
              const safeLink = getSafeNotificationLink(notification.link);

              return (
                <React.Fragment key={notification.id}>
                  <MenuItem
                    data-testid={`notification-item-${notification.id}`}
                    onClick={() => handleCardClick(notification.id, safeLink)}
                    aria-label={
                      safeLink
                        ? `${notification.title}. Opens ${safeLink}`
                        : notification.title
                    }
                    sx={{
                      backgroundColor: isSeen
                        ? "#FFFFFF"
                        : "rgba(239, 68, 68, 0.05)",
                      py: { xs: 1.25, sm: 1.5 },
                      px: { xs: 1.5, sm: 2 },
                      borderLeft: isSeen ? "none" : "3px solid #EF4444",
                      cursor: safeLink ? "pointer" : "default",
                      "&:hover": {
                        backgroundColor: isSeen
                          ? "rgba(107, 114, 128, 0.05)"
                          : "rgba(239, 68, 68, 0.1)",
                      },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      whiteSpace: "normal",
                      minHeight: { xs: 60, sm: 70 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        width: "100%",
                        gap: 1,
                      }}
                    >
                      <Typography
                        data-testid="notification-item-title-heading"
                        variant="subtitle2"
                        sx={{
                          fontWeight: isSeen ? 400 : 600,
                          color: isSeen ? "#6B7280" : "#1F2937",
                          fontSize: { xs: "0.8rem", sm: "0.8125rem" },
                          flex: 1,
                          wordBreak: "break-word",
                        }}
                      >
                        {notification.title}
                      </Typography>
                      {!isSeen && (
                        <Box
                          data-testid="notification-item-unread-indicator-div"
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "#EF4444",
                            flexShrink: 0,
                            mt: 0.5,
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      data-testid="notification-item-message-paragraph"
                      variant="body2"
                      sx={{
                        color: isSeen ? "#9CA3AF" : "#4B5563",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        mt: 0.5,
                        lineHeight: 1.4,
                        wordBreak: "break-word",
                      }}
                    >
                      {notification.body}
                    </Typography>
                    <Typography
                      data-testid="notification-item-time-span"
                      variant="caption"
                      sx={{
                        color: "#9CA3AF",
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        mt: 0.5,
                      }}
                    >
                      {timeAgo}
                    </Typography>
                  </MenuItem>
                  {index < latestNotifications.length - 1 && (
                    <Divider sx={{ my: 0 }} />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}
        {!notificationsLoading && (
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "#FFFFFF",
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <MenuItem
              data-testid="notifications-view-all-button"
              onClick={handleViewAllClick}
              sx={{
                justifyContent: "center",
                py: { xs: 1, sm: 1.25 },
                color: "#E80029",
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                "&:hover": { backgroundColor: "rgba(232, 0, 41, 0.05)" },
              }}
            >
              View all notifications
            </MenuItem>
          </Box>
        )}
      </Menu>
    </>
  );
}
